import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OpenInGhlButton } from "@/components/OpenInGhlButton";
import { useContactMessages, type ContactMessage } from "@/hooks/useContactMessages";
import { formatRelativeTime } from "@/lib/format-relative-time";

interface ContactConversationProps {
  contactId: string;
  ghlContactId?: string | null;
  ghlLocationId?: string | null;
}

const CHANNEL_LABELS: Record<string, string> = {
  sms: "SMS",
  email: "Email",
  call: "Call",
  other: "Other",
};

function channelLabel(channel: string): string {
  return CHANNEL_LABELS[channel] ?? "Other";
}

function directionLabel(direction: string | null): string {
  if (direction === "inbound") return "Customer";
  if (direction === "outbound") return "Us";
  return "Unknown";
}

// franklycatering #1405 — read-only thread for a contact's GHL conversation.
// Renders only the plain-text `body`; the raw-HTML column is never selected
// by useContactMessages, let alone rendered here.
export function ContactConversation({ contactId, ghlContactId, ghlLocationId }: ContactConversationProps) {
  const { data, isPending, isError, refetch } = useContactMessages(contactId);

  // The hook already orders by sent_at desc, but re-sort here so the UI never
  // silently depends on the fetch preserving that order.
  const messages = useMemo(() => {
    const rows = data ?? [];
    return [...rows].sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
  }, [data]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Conversation</h3>
        <OpenInGhlButton ghlContactId={ghlContactId} ghlLocationId={ghlLocationId} />
      </div>

      {isPending ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading messages…
        </div>
      ) : isError ? (
        <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
          <span>Couldn't load messages.</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
          No messages stored yet. New GHL messages show up here within a few minutes.
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-2">
          {messages.map((m) => (
            <MessageItem key={m.id} message={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MessageItem({ message }: { message: ContactMessage }) {
  const sentAt = new Date(message.sent_at);
  const relative = formatRelativeTime(message.sent_at);
  const isEmail = message.channel === "email";
  const attachments = (message.attachments ?? []).filter(
    (a) => /^https?:\/\//i.test(a.url),
  );

  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant={message.direction === "outbound" ? "secondary" : "outline"}>
            {directionLabel(message.direction)}
          </Badge>
          <span className="text-xs text-muted-foreground">{channelLabel(message.channel)}</span>
        </div>
        <span className="text-xs text-muted-foreground" title={relative ?? undefined}>
          {sentAt.toLocaleString()}
        </span>
      </div>
      {isEmail && message.subject && <div className="text-sm font-medium">{message.subject}</div>}
      {message.body && <div className="text-sm whitespace-pre-wrap break-words">{message.body}</div>}
      {attachments.length > 0 && (
        <div className="flex flex-col gap-1">
          {attachments.map((a, i) => (
            <a
              key={`${a.url}-${i}`}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline break-all"
            >
              {a.type || "Attachment"}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
